import { Data, DataSet, ObjectKey } from "datasheets/model/core/Workbook";
import { uuid } from "datasheets/model/utils/uuid";
import {
  dictionaryEntryMap,
  filterDict,
  filterDictByValue,
} from "datasheets/model/utils/dictionaryUtils";
import { NodeApi } from "react-arborist";
import { getOrCreateOrder } from "datasheets/model/logic/insertDeleteRows";
import { moveRow } from "datasheets/model/logic/moveRow";
import { TreeItem, childColumnName, parentColumnName } from "../TreeView";

export function treeViewHandleCreate(
  nameColumnId: string,
  setSourceDataset: (update: (data: DataSet) => DataSet) => void,
  setHierarchy: (update: (data: DataSet) => DataSet) => void,
  childKey: string,
  parentKey: string,
  parentId: string | null,
  setSelectedObject: (
    update: string | ((prev: string | null) => string | null) | null
  ) => void
) {
  const newRowId = uuid();
  const rowData = { id: newRowId, [nameColumnId]: "" };
  setSourceDataset((dataset: DataSet) => {
    const newDataset = datasetAddRow(dataset, newRowId, rowData);
    return newDataset;
  });
  setHierarchy((hierarchy: DataSet) => {
    const newTreeNode = treeDataAddNode(
      hierarchy,
      childKey,
      newRowId,
      parentKey,
      parentId
    );
    return newTreeNode;
  });
  setSelectedObject(newRowId);
  return { id: newRowId };
}
export function treeViewHandleDelete(
  setSourceData: (fn: (data: Data) => Data) => void,
  ids: string[],
  setHierarchy: (update: (data: DataSet) => DataSet) => void,
  parentKey: string,
  childKey: string
) {
  setSourceData((data: Data) => {
    return filterDict(data, (key: string) => !ids.includes(key));
  });
  setHierarchy((hierarchy: DataSet) => {
    const newData = filterDictByValue(
      hierarchy.data,
      (row) => !(ids.includes(row[parentKey]) || ids.includes(row[childKey]))
    );
    return { ...hierarchy, ...{ data: newData } };
  });
}
export function treeViewHandleDrop(
  index: number,
  siblings: NodeApi<TreeItem>[] | TreeItem[] | null,
  parentId: string | null,
  orderingLookup: { [key: string]: any },
  setSourceDataset: (update: (data: DataSet) => DataSet) => void,
  dragIds: string[],
  setHierarchy: (update: (data: DataSet) => DataSet) => void,
  childKey: string,
  parentKey: string
) {
  if (index > 0) {
    console.log("INDEX:", index);
  }
  // Find insertion location
  let insertAfterId: string | null = null;
  if (index === 0) {
    // Insert after parent
    insertAfterId = parentId;
  } else {
    // Insert after previous sibling
    if (siblings) {
      insertAfterId = siblings[index - 1].id;
    } else {
      insertAfterId = null;
    }
  }
  let insertAfter = !!insertAfterId ? orderingLookup[insertAfterId] : 0;
  // Reorder underlying data
  setSourceDataset((sourceDataset: DataSet) => {
    let newDataset = sourceDataset;
    dragIds.forEach((dragId) => {
      const fromIndex = orderingLookup[dragId];
      newDataset = moveRow(fromIndex, insertAfter)(newDataset);
      insertAfter = fromIndex;
    });
    return newDataset;
  });
  // Update hierarchy dataset
  setHierarchy((hierarchy: DataSet) => {
    // Calculate mapping from dragId to rowId
    const dragIdToRowId = Object.entries(hierarchy.data).reduce(
      (prev: { [key: string]: string }, [rowId, row]) => {
        const childId: string = row[childKey];
        prev[childId] = rowId;
        return prev;
      },
      {}
    );
    let newData = hierarchy.data;
    dragIds.forEach((dragId) => {
      // TODO: make this more efficient
      const rowKey = dragIdToRowId[dragId] ?? uuid();
      const mappingObjectForChild = hierarchy.data[rowKey] ?? {
        [childKey]: dragId,
      };
      const newMappingObject = {
        ...mappingObjectForChild,
        ...{ [parentKey]: parentId ?? "root" }, // We set to root if parent is null. Note, alternatively, we could delete this entry
      };
      newData = { ...newData, ...{ [rowKey]: newMappingObject } };
    });
    return { ...hierarchy, ...{ data: newData } };
  });
}
function treeDataAddNode(
  hierarchy: DataSet,
  childKey: string,
  newRowId: string,
  parentKey: string,
  parentId: string | null
) {
  const newData = {
    ...hierarchy.data,
    ...{ [uuid()]: { [childKey]: newRowId, [parentKey]: parentId } },
  };
  const newTreeNode = { ...hierarchy, ...{ data: newData } };
  return newTreeNode;
}
function datasetAddRow(
  dataset: DataSet,
  newRowId: string,
  rowData: { [x: string]: string; id: string }
) {
  const newData = { ...dataset.data, ...{ [newRowId]: rowData } };
  const naturalOrder = getOrCreateOrder(dataset);
  const lastRow = Math.max(
    ...Object.keys(naturalOrder).map((row) => parseInt(row))
  );
  naturalOrder[lastRow + 1] = newRowId;
  const newDataset = {
    ...dataset,
    ...{ data: newData, naturalOrder: naturalOrder },
  };
  return newDataset;
}
export function convertChildMapToTreeHierarchy(
  nodeMap: { [parent: string]: string[] },
  sourceData: Data,
  nameColumnId: string
): { [key: string]: TreeItem } {
  return dictionaryEntryMap(
    nodeMap,
    (itemKey: ObjectKey, children: ObjectKey[]) => {
      return {
        [itemKey]: {
          id: itemKey,
          children: children,
          name:
            itemKey in sourceData ? sourceData[itemKey][nameColumnId] : "root",
        },
      };
    }
  );
}
export function convertHierarchyTableDataToMapOfParentsToChildren(
  hierarchyData: Data,
  parentKey: string,
  childKey: string
): { [parent: string]: string[] } {
  return Object.values(hierarchyData).reduce((prev, row) => {
    const parentVal = row[parentKey];
    const childVal = row[childKey];
    const children: string[] = parentVal in prev ? prev[parentVal] : [];
    children.push(childVal);
    prev[parentVal] = children;
    if (!(childVal in prev)) {
      prev[childVal] = []; // Create an entry in case we don't see it again
    }
    return prev;
  }, {});
}

export function getParentFromChild(
  childId: string,
  hierarchyData: Data,
  parentKey: string,
  childKey: string
): string | null {
  const childRows = Object.values(hierarchyData).filter(
    (row) => row[childKey] === childId
  );
  if (!childRows || childRows.length === 0) {
    return null; // Return null if no parent registered for child
  }
  if (childRows.length > 1) {
    console.error("Child in tree hierarchy has multiple parents:", childId);
  }
  // Ignore situation where we have multiple parents (should not happen for tree view) and just return
  return childRows[0][parentKey];
}
// Export for testing

export function populateHierarchyWithNewData(
  hierarchy: DataSet,
  data: Data
): Data {
  const childKey = hierarchy.properties.nameToKey[childColumnName];
  const parentKey = hierarchy.properties.nameToKey[parentColumnName];
  let newHierarchyData = filterDictByValue(
    hierarchy.data,
    (object) => object[parentKey] in data
  );
  newHierarchyData = filterDictByValue(
    newHierarchyData,
    (object) => object[childKey] in data
  );
  const childrenWithParents = Object.values(newHierarchyData).map(
    (object) => object[childKey]
  );
  Object.keys(data).forEach((objectKey: ObjectKey) => {
    if (!childrenWithParents.includes(objectKey)) {
      newHierarchyData[uuid()] = { [childKey]: objectKey, [parentKey]: "root" };
    }
  });
  return newHierarchyData;
}
